import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import CategorySelectWithAdd from "../../components/CategorySelectWithAdd";
import { Plus, Trash2, Sparkles, Loader2, X, Image as ImageIcon, CheckCircle2, AlertCircle, Settings2 } from "lucide-react";
import { toast, Toaster } from "sonner";

const newRow = () => ({
    uid: Math.random().toString(36).slice(2),
    productImage: null,        // File
    compositionImage: null,    // File
    dosageImage: null,         // File
    extraImages: [],           // File[] — additional gallery photos (up to 10 total per item)
    fallbackName: "",
    extracted: null,           // { name, category, composition, dosage, indications, short_description, description, confidence, notes }
    customSizes: null,         // null = use batch defaults; otherwise { [size_id]: {selected, price} }
    extracting: false,
    writingCopy: false,        // true while POST /admin/products/ai-write-description is in flight
    error: null,
    saved: false,
});

const previewUrl = (file) => (file ? URL.createObjectURL(file) : "");

/** Build the effective sizes object, merging defaults with custom-row overrides. */
const effectiveSizes = (row, defaults) => row.customSizes || defaults;

/** Convert a {size_id: {selected, price}} map into [{size_id, size_label, price, stock, sku}] variants. */
const buildVariants = (mapValue, sizes) => {
    const out = [];
    for (const s of sizes) {
        const r = mapValue?.[s.id];
        if (r?.selected && Number(r.price) > 0) {
            out.push({
                size_id: s.id,
                size_label: s.label || `${s.ml}ml`,
                price: Number(r.price),
                stock: 0,
                sku: "",
            });
        }
    }
    return out;
};

export default function AdminBulkProducts() {
    const [rows, setRows] = useState([newRow()]);
    const [categories, setCategories] = useState([]);
    const [sizes, setSizes] = useState([]);
    /** Batch defaults: { [size_id]: { selected: bool, price: number } } */
    const [defaultSizes, setDefaultSizes] = useState({});
    const [savingAll, setSavingAll] = useState(false);
    const fileRefs = useRef({});

    useEffect(() => {
        Promise.all([api.get("/categories"), api.get("/sizes")]).then(([c, s]) => {
            setCategories(c.data);
            setSizes(s.data);
            // Pre-select the smallest size by default with empty price (admin must enter)
            if (s.data?.[0]) {
                setDefaultSizes({ [s.data[0].id]: { selected: true, price: 0 } });
            }
        });
    }, []);

    const reloadCategories = async () => {
        const c = await api.get("/categories");
        setCategories(c.data);
    };

    const updateRow = (uid, patch) => setRows((r) => r.map((row) => row.uid === uid ? { ...row, ...patch } : row));
    const removeRow = (uid) => setRows((r) => r.length === 1 ? [newRow()] : r.filter((x) => x.uid !== uid));

    const addExtraImages = (uid, fileList) => {
        const files = Array.from(fileList || []);
        if (!files.length) return;
        setRows((r) => r.map((row) => {
            if (row.uid !== uid) return row;
            const accepted = [];
            for (const f of files) {
                if (f.size > 6 * 1024 * 1024) {
                    toast.error(`${f.name} is ${(f.size / 1024 / 1024).toFixed(1)} MB. Each picture must be under 6 MB.`);
                    continue;
                }
                if (!/^image\/(jpeg|jpg|png|webp)$/.test(f.type)) {
                    toast.error(`${f.name}: only JPEG / PNG / WEBP supported`);
                    continue;
                }
                accepted.push(f);
            }
            if (!accepted.length) return row;
            const next = [...(row.extraImages || []), ...accepted];
            // Soft cap at 15 extras (so total ≤ 18 incl. product/composition/dosage)
            if (next.length > 15) {
                toast.message(`Kept first 15 extra photos — that's plenty for one product.`);
                next.length = 15;
            }
            return { ...row, extraImages: next };
        }));
    };

    const removeExtraImage = (uid, idx) => {
        setRows((r) => r.map((row) => {
            if (row.uid !== uid) return row;
            const next = [...(row.extraImages || [])];
            next.splice(idx, 1);
            return { ...row, extraImages: next };
        }));
    };

    const defaultSelectedCount = useMemo(
        () => Object.values(defaultSizes).filter((s) => s?.selected && Number(s.price) > 0).length,
        [defaultSizes],
    );

    const extractRow = async (row) => {
        if (!row.productImage) { toast.error("Pick a product image first"); return null; }
        updateRow(row.uid, { extracting: true, error: null });
        const fd = new FormData();
        fd.append("product_image", row.productImage);
        if (row.compositionImage) fd.append("composition_image", row.compositionImage);
        if (row.dosageImage) fd.append("dosage_image", row.dosageImage);
        if (row.fallbackName) fd.append("fallback_name", row.fallbackName);
        try {
            const { data } = await api.post("/admin/bulk-products/extract", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 90000,
            });
            updateRow(row.uid, { extracted: data, extracting: false });
            toast.success(`Extracted: ${data.name || "(no name)"}`);
            return data;
        } catch (e) {
            const msg = e.response?.data?.detail || e.message || "Extraction failed";
            updateRow(row.uid, { extracting: false, error: msg });
            toast.error(msg);
            return null;
        }
    };

    const extractAll = async () => {
        for (const row of rows) {
            if (!row.extracted && row.productImage && !row.saved) {
                // run sequentially to avoid hammering the LLM
                // eslint-disable-next-line no-await-in-loop
                await extractRow(row);
            }
        }
    };

    /** Call the AI-write-description endpoint for ONE row, using either the
     *  passed-in `extraction` (preferred — avoids React state-staleness) or
     *  falling back to whatever's stored on `row.extracted`. Patches the
     *  extracted object on the row with `short_description` and `description`
     *  so the existing save payload picks them up automatically. */
    const writeCopyRow = async (row, extraction = null) => {
        const ex = extraction || row.extracted;
        if (!ex) return null;
        updateRow(row.uid, { writingCopy: true });
        try {
            const catName = (categories.find((c) => c.id === ex.category_id) || {}).name || ex.category || "";
            const { data } = await api.post("/admin/products/ai-write-description", {
                name: ex.name || "",
                composition: ex.composition || "",
                indications: ex.indications || "",
                dosage: ex.dosage || "",
                category: catName,
            }, { timeout: 60000 });
            const tagline = (data.tagline || "").trim();
            const longCopy = tagline ? `${tagline}\n\n${data.description || ""}` : (data.description || "");
            const enriched = {
                ...ex,
                short_description: data.short_description || ex.short_description || "",
                description: longCopy || ex.description || "",
                copy_notes: data.notes || "",
            };
            updateRow(row.uid, { writingCopy: false, extracted: enriched });
            return enriched;
        } catch (e) {
            updateRow(row.uid, { writingCopy: false });
            toast.error(`${ex.name || "row"}: AI write failed — ${e.response?.data?.detail || e.message}`);
            return null;
        }
    };

    /** Auto-fill EVERYTHING — Extract then Write-copy for every eligible row.
     *  Runs sequentially per LLM, but uses direct data passing so each row's
     *  Write-copy step sees its own fresh extraction without waiting for
     *  React state to commit. */
    const enrichAll = async () => {
        let extracted = 0, written = 0;
        for (const row of rows) {
            if (row.saved) continue;
            // Step A: extract if missing (and we have a photo).
            let ex = row.extracted;
            if (!ex && row.productImage) {
                // eslint-disable-next-line no-await-in-loop
                ex = await extractRow(row);
                if (ex) extracted += 1;
            }
            // Step B: write copy if extracted but no description yet.
            if (ex && !ex.description) {
                // eslint-disable-next-line no-await-in-loop
                const result = await writeCopyRow(row, ex);
                if (result) written += 1;
            }
        }
        if (extracted === 0 && written === 0) {
            toast.message("Nothing new to enrich — every row already has copy.");
        } else {
            toast.success(`Auto-fill complete · ${extracted} extracted, ${written} copy written. Review each row before saving.`);
        }
    };

    /** Upload a single image file via the staff upload endpoint and return its URL. */
    const uploadProductImage = async (file) => {
        if (!file) return null;
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        // /uploads returns { path, url }. The `url` is already root-relative (/api/uploads/...).
        return data.url;
    };

    /** Find an existing category by case-insensitive name; otherwise create one. */
    const ensureCategory = async (suggestedName) => {
        const cleaned = (suggestedName || "").trim();
        if (!cleaned) return categories[0]?.id || null;
        const match = categories.find((c) => c.name.toLowerCase() === cleaned.toLowerCase());
        if (match) return match.id;
        // Create a new category on the fly — keeps the catalog in sync with what AI suggested.
        try {
            const { data } = await api.post("/categories", { name: cleaned, slug: "" });
            setCategories((prev) => prev.some((c) => c.id === data.id) ? prev : [...prev, data]);
            return data.id;
        } catch (e) {
            // If creation failed (e.g. race / duplicate), fall back to first category so the save still succeeds.
            toast.message(`Couldn't create category "${cleaned}" — using "${categories[0]?.name}"`);
            return categories[0]?.id || null;
        }
    };

    const saveOne = async (row) => {
        if (!row.extracted) return null;
        const ex = row.extracted;
        if (!ex.name?.trim()) { toast.error("Name is required"); return null; }

        // 1. Variants check — must have at least one size with a real price.
        const variants = buildVariants(effectiveSizes(row, defaultSizes), sizes);
        if (variants.length === 0) {
            toast.error(`${ex.name}: pick at least one size with a price (use the "Sizes & prices" matrix at the top, or click "Custom sizes" on this row).`);
            return null;
        }

        // 2. Upload the product image (and composition / dosage labels too — handy for re-OCR audits later).
        //    Plus any additional gallery photos the admin picked.
        let images = [];
        try {
            const productUrl = await uploadProductImage(row.productImage);
            if (productUrl) images.push(productUrl);
            // Composition + dosage labels stored as extra catalog images — admin can reorder later.
            const compUrl = await uploadProductImage(row.compositionImage);
            if (compUrl) images.push(compUrl);
            const doseUrl = await uploadProductImage(row.dosageImage);
            if (doseUrl) images.push(doseUrl);
            // Additional gallery photos
            for (const f of row.extraImages || []) {
                // eslint-disable-next-line no-await-in-loop
                const url = await uploadProductImage(f);
                if (url) images.push(url);
            }
        } catch (e) {
            toast.error(`${ex.name}: image upload failed — ${e.response?.data?.detail || e.message}`);
            return null;
        }

        // 3. Resolve / create the category so the frontend filters match.
        const catId = ex.category_id || await ensureCategory(ex.category);
        if (!catId) { toast.error("Create at least one category before saving"); return null; }

        // 4. Build the payload. If AI-write step ran, prefer those polished
        //    descriptions; otherwise fall back to indications as before.
        const aiShort = (ex.short_description || "").trim();
        const aiLong = (ex.description || "").trim();
        const payload = {
            name: ex.name.trim(),
            description: aiLong || ex.indications || "",
            short_description: aiShort || (ex.indications || "").slice(0, 140),
            category_id: catId,
            composition: ex.composition || "",
            indications: (ex.indications || "").split(/[,;]/).map((s) => s.trim()).filter(Boolean).join(", "),
            dosage: ex.dosage || "",
            images,
            variants,
            active: true,
        };
        try {
            const { data } = await api.post("/products", payload);
            updateRow(row.uid, { saved: true, savedId: data.id });
            return data;
        } catch (e) {
            toast.error(`${ex.name}: ${e.response?.data?.detail || "Save failed"}`);
            return null;
        }
    };

    const saveAll = async () => {
        setSavingAll(true);
        let ok = 0;
        for (const row of rows) {
            if (row.extracted && !row.saved) {
                // eslint-disable-next-line no-await-in-loop
                const r = await saveOne(row);
                if (r) ok += 1;
            }
        }
        setSavingAll(false);
        if (ok > 0) toast.success(`Created ${ok} product${ok === 1 ? "" : "s"}`);
    };

    const fileInput = (uid, key, label) => {
        const inputId = `file-${uid}-${key}`;
        const file = rows.find((r) => r.uid === uid)?.[key];
        return (
            <label className="block">
                <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">{label}</div>
                {file ? (
                    <div className="relative w-full aspect-square border border-stone-300 bg-stone-50 group" data-testid={`preview-${uid}-${key}`}>
                        <img src={previewUrl(file)} alt={label} className="w-full h-full object-cover" />
                        <button type="button" onClick={() => updateRow(uid, { [key]: null })} className="absolute top-1 right-1 bg-white/90 p-1 opacity-0 group-hover:opacity-100 transition" aria-label={`Remove ${label}`}>
                            <X className="w-3 h-3" />
                        </button>
                    </div>
                ) : (
                    <button type="button" onClick={() => fileRefs.current[inputId]?.click()} className="w-full aspect-square border-2 border-dashed border-stone-300 hover:border-sky-600 hover:bg-sky-50 transition flex flex-col items-center justify-center text-stone-400 text-xs gap-1" data-testid={`pick-${uid}-${key}`}>
                        <ImageIcon className="w-5 h-5" />
                        <span>Choose</span>
                    </button>
                )}
                <input
                    ref={(el) => { fileRefs.current[inputId] = el; }}
                    id={inputId}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) {
                            if (f.size > 6 * 1024 * 1024) {
                                toast.error(`${f.name} is ${(f.size / 1024 / 1024).toFixed(1)} MB. Each picture must be under 6 MB.`);
                                e.target.value = "";
                                return;
                            }
                            updateRow(uid, { [key]: f });
                        }
                        e.target.value = "";
                    }}
                />
            </label>
        );
    };

    const totalReady = rows.filter((r) => r.extracted && !r.saved).length;
    const totalToExtract = rows.filter((r) => !r.extracted && r.productImage && !r.saved).length;
    const totalToWrite = rows.filter((r) => r.extracted && !r.extracted.description && !r.saved).length;
    const totalToEnrich = rows.filter((r) => !r.saved && (
        (!r.extracted && r.productImage) ||
        (r.extracted && !r.extracted.description)
    )).length;
    const enrichingAny = rows.some((r) => r.extracting || r.writingCopy);

    return (
        <div data-testid="admin-bulk-products">
            <Toaster position="top-center" />
            <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow text-sky-700">AI assisted</div>
                    <h1 className="font-serif text-4xl text-slate-900 mt-2">Bulk add from images</h1>
                    <p className="text-sm text-stone-600 mt-2 max-w-2xl">
                        Upload product photos along with composition and dosage labels. Claude vision reads the labels and pre-fills name, category, composition and dosage. Review, fix anything that&apos;s off, then save.
                    </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <Button variant="outline" onClick={() => setRows((r) => [...r, newRow()])} className="rounded-none border-stone-300" data-testid="add-row"><Plus className="w-4 h-4 mr-2" />Add another</Button>
                    <Button onClick={extractAll} disabled={totalToExtract === 0 || enrichingAny} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="extract-all"><Sparkles className="w-4 h-4 mr-2" />Extract {totalToExtract > 0 ? `(${totalToExtract})` : "all"}</Button>
                    <Button
                        onClick={enrichAll}
                        disabled={totalToEnrich === 0 || enrichingAny}
                        className="rounded-none bg-emerald-700 hover:bg-emerald-800 text-stone-50"
                        data-testid="enrich-all"
                        title="Extract + Write description for every eligible row in one go"
                    >
                        {enrichingAny ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Working…</> : <><Sparkles className="w-4 h-4 mr-2" />Auto-fill everything {totalToEnrich > 0 ? `(${totalToEnrich})` : ""}</>}
                    </Button>
                    <Button onClick={saveAll} disabled={totalReady === 0 || savingAll} className="rounded-none bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-all">
                        {savingAll ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : <><CheckCircle2 className="w-4 h-4 mr-2" />Save {totalReady > 0 ? `${totalReady} ` : ""}products</>}
                    </Button>
                </div>
            </div>

            {/* Sizes & prices matrix — applies to every product unless overridden per row */}
            <div className="bg-amber-50 border border-amber-200 px-4 py-4 mb-6 text-sm">
                <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
                    <div>
                        <Label className="text-[10px] uppercase tracking-widest text-stone-500">Sizes & prices for the whole batch</Label>
                        <p className="text-xs text-stone-600 mt-1">Tick the sizes you want every new product to have, and set a price for each. Stock starts at 0 — update it from <em>Stock</em> later. Each row below can override these defaults.</p>
                    </div>
                    <span className="text-[10px] uppercase tracking-widest text-emerald-700">{defaultSelectedCount} size{defaultSelectedCount === 1 ? "" : "s"} ready</span>
                </div>
                <SizesMatrix
                    sizes={sizes}
                    value={defaultSizes}
                    onChange={setDefaultSizes}
                    testIdPrefix="bulk-default-sizes"
                />
            </div>

            <div className="space-y-4">
                {rows.map((row, idx) => (
                    <div key={row.uid} className={`bg-white border ${row.saved ? "border-emerald-300" : row.error ? "border-red-300" : "border-sky-100"} p-5`} data-testid={`bulk-row-${idx}`}>
                        <div className="flex items-start gap-4 flex-wrap">
                            <div className="text-[10px] uppercase tracking-widest text-sky-700 shrink-0">Item {idx + 1}</div>
                            <button type="button" onClick={() => removeRow(row.uid)} className="ml-auto text-stone-400 hover:text-red-600" aria-label="Remove item" data-testid={`remove-row-${idx}`}><Trash2 className="w-4 h-4" /></button>
                        </div>

                        <div className="grid grid-cols-3 gap-3 mt-3">
                            {fileInput(row.uid, "productImage", "Product photo *")}
                            {fileInput(row.uid, "compositionImage", "Composition label")}
                            {fileInput(row.uid, "dosageImage", "Dosage label")}
                        </div>

                        {/* Additional gallery photos — up to 10+ per item */}
                        <ExtraPhotosPicker
                            row={row}
                            onAdd={(files) => addExtraImages(row.uid, files)}
                            onRemove={(idx) => removeExtraImage(row.uid, idx)}
                            testIdPrefix={`extras-${idx}`}
                        />

                        <div className="mt-3">
                            <Label className="text-[10px] uppercase tracking-widest text-stone-500">Name hint (optional)</Label>
                            <Input value={row.fallbackName} onChange={(e) => updateRow(row.uid, { fallbackName: e.target.value })} placeholder="If the product photo doesn't show a clear name, type it here" className="rounded-none border-stone-300 mt-1" data-testid={`hint-${idx}`} />
                        </div>

                        <div className="mt-4 flex flex-wrap gap-2">
                            <Button variant="outline" disabled={!row.productImage || row.extracting} onClick={() => extractRow(row)} className="rounded-none border-sky-700 text-sky-700 hover:bg-sky-50" data-testid={`extract-${idx}`}>
                                {row.extracting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Reading labels…</> : <><Sparkles className="w-4 h-4 mr-2" />{row.extracted ? "Re-extract" : "Extract with AI"}</>}
                            </Button>
                            {row.extracted && !row.saved && (
                                <Button onClick={() => saveOne(row)} className="rounded-none bg-amber-600 hover:bg-amber-700 text-white" data-testid={`save-${idx}`}><CheckCircle2 className="w-4 h-4 mr-2" />Save this product</Button>
                            )}
                            {row.saved && <span className="inline-flex items-center text-sm text-emerald-700"><CheckCircle2 className="w-4 h-4 mr-1" />Saved</span>}
                            {row.error && <span className="inline-flex items-center text-sm text-red-700"><AlertCircle className="w-4 h-4 mr-1" />{row.error}</span>}
                        </div>

                        {row.extracted && (
                            <div className="mt-4 grid sm:grid-cols-2 gap-3 bg-sky-50/40 border border-sky-100 p-4">
                                <div className="sm:col-span-2 flex items-center justify-between">
                                    <div className="text-xs uppercase tracking-widest text-sky-700">AI extraction · editable</div>
                                    <div className="text-xs text-stone-500">Confidence: <span className={`font-medium ${row.extracted.confidence === "high" ? "text-emerald-700" : row.extracted.confidence === "medium" ? "text-amber-700" : "text-red-700"}`}>{row.extracted.confidence || "—"}</span></div>
                                </div>
                                <Field label="Name *" v={row.extracted.name} on={(v) => updateRow(row.uid, { extracted: { ...row.extracted, name: v } })} testId={`ex-name-${idx}`} />
                                <div>
                                    <Label className="text-[10px] uppercase tracking-widest text-stone-500">Category *</Label>
                                    <div className="mt-1">
                                        <CategorySelectWithAdd
                                            value={row.extracted.category_id || (categories.find((c) => c.name.toLowerCase() === (row.extracted.category || "").toLowerCase())?.id || "")}
                                            onChange={(id) => {
                                                const cat = categories.find((c) => c.id === id);
                                                updateRow(row.uid, { extracted: { ...row.extracted, category_id: id, category: cat?.name || row.extracted.category } });
                                            }}
                                            categories={categories}
                                            onCategoriesChanged={reloadCategories}
                                            testId={`ex-category-${idx}`}
                                            placeholder={row.extracted.category ? `AI suggested: ${row.extracted.category}` : "Choose…"}
                                        />
                                    </div>
                                    {row.extracted.category && !categories.find((c) => c.name.toLowerCase() === row.extracted.category.toLowerCase()) && !row.extracted.category_id && (
                                        <p className="text-[10px] text-amber-700 mt-1">AI suggested “{row.extracted.category}” — pick an existing category or hit “+ Add new category”.</p>
                                    )}
                                </div>
                                <FieldArea label="Composition" v={row.extracted.composition} on={(v) => updateRow(row.uid, { extracted: { ...row.extracted, composition: v } })} testId={`ex-composition-${idx}`} />
                                <FieldArea label="Dosage / Directions" v={row.extracted.dosage} on={(v) => updateRow(row.uid, { extracted: { ...row.extracted, dosage: v } })} testId={`ex-dosage-${idx}`} />
                                <Field label="Indications (comma-separated)" v={row.extracted.indications} on={(v) => updateRow(row.uid, { extracted: { ...row.extracted, indications: v } })} testId={`ex-indications-${idx}`} />
                                {row.extracted.notes && <div className="sm:col-span-2 text-xs text-stone-500">Note: {row.extracted.notes}</div>}

                                {/* AI Write copy — converts facts above into marketing-ready short + full descriptions. */}
                                <div className="sm:col-span-2 border-t border-sky-100 pt-3 mt-1">
                                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                                        <div>
                                            <Label className="text-[10px] uppercase tracking-widest text-emerald-700">AI marketing copy</Label>
                                            <p className="text-[11px] text-stone-500">
                                                {row.extracted.description
                                                    ? "Short summary + long description written by AI. Edit below if needed."
                                                    : "Click to draft a tagline, short summary and full marketing description from the facts above."}
                                            </p>
                                        </div>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            disabled={row.writingCopy || !row.extracted.name}
                                            onClick={() => writeCopyRow(row)}
                                            className="rounded-none border-emerald-700 text-emerald-700 hover:bg-emerald-50"
                                            data-testid={`write-copy-${idx}`}
                                        >
                                            {row.writingCopy
                                                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Writing…</>
                                                : <><Sparkles className="w-4 h-4 mr-2" />{row.extracted.description ? "Re-write copy" : "Write copy with AI"}</>}
                                        </Button>
                                    </div>
                                    {row.extracted.description && (
                                        <>
                                            <Field
                                                label="Short summary (shown on shop cards)"
                                                v={row.extracted.short_description}
                                                on={(v) => updateRow(row.uid, { extracted: { ...row.extracted, short_description: v } })}
                                                testId={`ex-short-${idx}`}
                                            />
                                            <div className="mt-2">
                                                <Label className="text-[10px] uppercase tracking-widest text-stone-500">Full description</Label>
                                                <Textarea
                                                    value={row.extracted.description}
                                                    onChange={(e) => updateRow(row.uid, { extracted: { ...row.extracted, description: e.target.value } })}
                                                    rows={6}
                                                    className="rounded-none border-stone-300 mt-1 bg-white whitespace-pre-line"
                                                    data-testid={`ex-desc-${idx}`}
                                                />
                                            </div>
                                            {row.extracted.copy_notes && <div className="text-[11px] text-stone-500 mt-1 italic">AI note: {row.extracted.copy_notes}</div>}
                                        </>
                                    )}
                                </div>

                                {/* Per-row sizes override */}
                                <div className="sm:col-span-2 border-t border-sky-100 pt-3 mt-1">
                                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                                        <div>
                                            <Label className="text-[10px] uppercase tracking-widest text-stone-500">Sizes for this product</Label>
                                            <p className="text-[11px] text-stone-500">
                                                {row.customSizes
                                                    ? "Custom sizes — overrides the batch defaults above."
                                                    : `Using batch defaults (${defaultSelectedCount} size${defaultSelectedCount === 1 ? "" : "s"}).`}
                                            </p>
                                        </div>
                                        {row.customSizes ? (
                                            <button
                                                type="button"
                                                onClick={() => updateRow(row.uid, { customSizes: null })}
                                                className="text-[11px] text-stone-500 hover:text-sky-700 underline"
                                                data-testid={`reset-sizes-${idx}`}
                                            >
                                                Reset to batch defaults
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => updateRow(row.uid, { customSizes: { ...defaultSizes } })}
                                                className="text-[11px] text-sky-700 hover:text-sky-800 inline-flex items-center gap-1"
                                                data-testid={`override-sizes-${idx}`}
                                            >
                                                <Settings2 className="w-3 h-3" />Custom sizes for this product
                                            </button>
                                        )}
                                    </div>
                                    {row.customSizes && (
                                        <SizesMatrix
                                            sizes={sizes}
                                            value={row.customSizes}
                                            onChange={(v) => updateRow(row.uid, { customSizes: v })}
                                            testIdPrefix={`row-sizes-${idx}`}
                                        />
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

const Field = ({ label, v, on, testId }) => (
    <div>
        <Label className="text-[10px] uppercase tracking-widest text-stone-500">{label}</Label>
        <Input value={v || ""} onChange={(e) => on(e.target.value)} className="rounded-none border-stone-300 mt-1 bg-white" data-testid={testId} />
    </div>
);
const FieldArea = ({ label, v, on, testId }) => (
    <div className="sm:col-span-2">
        <Label className="text-[10px] uppercase tracking-widest text-stone-500">{label}</Label>
        <Textarea value={v || ""} onChange={(e) => on(e.target.value)} rows={3} className="rounded-none border-stone-300 mt-1 bg-white" data-testid={testId} />
    </div>
);

/** Additional photos picker — gallery of extra images beyond the 3 AI-targeted slots.
 *  These photos are NOT sent to Claude vision; they're appended to the product's `images`
 *  array on save. Supports multi-pick. ≤ 6 MB each, soft cap of 15 per item. */
const ExtraPhotosPicker = ({ row, onAdd, onRemove, testIdPrefix }) => {
    const inputRef = useRef(null);
    const extras = row.extraImages || [];
    return (
        <div className="mt-4 border border-dashed border-stone-300 bg-stone-50/60 p-3" data-testid={`${testIdPrefix}-wrap`}>
            <div className="flex items-end justify-between flex-wrap gap-2 mb-2">
                <div>
                    <Label className="text-[10px] uppercase tracking-widest text-stone-500">Additional photos · gallery</Label>
                    <p className="text-[11px] text-stone-500 mt-0.5">
                        Add up to 10+ extra photos per product (back of box, lifestyle, certifications, etc.). These appear in the product gallery on the shop. Not sent to AI — just attached to the product.
                    </p>
                </div>
                <Button
                    type="button"
                    variant="outline"
                    onClick={() => inputRef.current?.click()}
                    className="rounded-none border-sky-300 text-sky-700 hover:bg-sky-50"
                    data-testid={`${testIdPrefix}-add`}
                >
                    <Plus className="w-4 h-4 mr-1" />Add photos
                </Button>
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    className="hidden"
                    onChange={(e) => { onAdd(e.target.files); e.target.value = ""; }}
                    data-testid={`${testIdPrefix}-input`}
                />
            </div>
            {extras.length === 0 ? (
                <p className="text-[11px] text-stone-400 italic">No extra photos yet — click <strong>Add photos</strong> to pick several at once.</p>
            ) : (
                <>
                    <p className="text-[11px] text-stone-500 mb-2">{extras.length} extra photo{extras.length === 1 ? "" : "s"} · hover to remove</p>
                    <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                        {extras.map((f, i) => (
                            <div key={`${i}-${f.name}`} className="relative aspect-square border border-stone-300 bg-white group" data-testid={`${testIdPrefix}-tile-${i}`}>
                                <img src={URL.createObjectURL(f)} alt={f.name} className="w-full h-full object-cover" />
                                <button
                                    type="button"
                                    onClick={() => onRemove(i)}
                                    className="absolute top-1 right-1 bg-white/95 p-1 opacity-0 group-hover:opacity-100 transition border border-stone-200 hover:border-red-500 hover:text-red-700"
                                    aria-label={`Remove ${f.name}`}
                                    data-testid={`${testIdPrefix}-remove-${i}`}
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

/** Multi-size matrix: a checkbox + size label + price input per available size. */
const SizesMatrix = ({ sizes, value, onChange, testIdPrefix }) => {
    if (!sizes?.length) {
        return <div className="text-xs text-stone-500">No sizes configured. Add sizes from <em>Admin → Sizes</em> first.</div>;
    }
    return (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {sizes.map((s) => {
                const r = value?.[s.id] || { selected: false, price: 0 };
                return (
                    <label
                        key={s.id}
                        className={`flex items-center gap-3 border px-3 py-2 transition-colors cursor-pointer ${r.selected ? "bg-white border-sky-300" : "bg-white/40 border-stone-200 hover:border-stone-300"}`}
                        data-testid={`${testIdPrefix}-row-${s.id}`}
                    >
                        <Checkbox
                            checked={r.selected}
                            onCheckedChange={(v) => onChange({ ...value, [s.id]: { ...r, selected: !!v } })}
                            data-testid={`${testIdPrefix}-cb-${s.id}`}
                        />
                        <span className="font-mono text-sm text-stone-700 w-16">{s.label || `${s.ml}ml`}</span>
                        <div className="flex items-center gap-1 flex-1">
                            <span className="text-stone-500 text-sm">₹</span>
                            <Input
                                type="number"
                                min={0}
                                step="0.5"
                                value={r.price || ""}
                                onChange={(e) => {
                                    const price = parseFloat(e.target.value || 0);
                                    onChange({ ...value, [s.id]: { selected: r.selected || price > 0, price } });
                                }}
                                placeholder="0"
                                className="rounded-none border-stone-300 h-8 text-sm"
                                data-testid={`${testIdPrefix}-price-${s.id}`}
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    </label>
                );
            })}
        </div>
    );
};
